import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApi } from "../lib/useApi";
import { type OrgInvite } from "../lib/api";
import { useSetTopbarTabs } from "../components/TopbarTabs";
import { CopyableEmail } from "../components/CopyableId";
import { ReissueButton } from "../components/ReissueButton";
import { StatusTag } from "../components/Tags";

export function OrganizationInvitationsPage() {
  const api = useApi();
  const navigate = useNavigate();
  useSetTopbarTabs([
    { id: "organizations", label: "Organizations", active: false, onSelect: () => navigate("/support/organizations") },
    { id: "invitations", label: "Invitations", active: true, onSelect: () => navigate("/support/organization-invitations") },
    { id: "create", label: "Create", active: false, onSelect: () => navigate("/support/create-organization") },
  ]);

  const [params, setParams] = useSearchParams();
  const email = params.get("email") ?? "";
  const [input, setInput] = useState(email);
  const [results, setResults] = useState<OrgInvite[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setInput(email); }, [email]);

  // No (or too-short) search term = "latest" mode: the backend returns the 10 most recent invites.
  const latestMode = email.length < 2;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.searchOrganizationInvites(latestMode ? undefined : email).then(
      (rows) => { if (!cancelled) setResults(rows); },
      (e) => { if (!cancelled) { setError(`${e}`); setResults(null); } },
    ).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, email, latestMode]);

  function submit(e: FormEvent) {
    e.preventDefault();
    const v = input.trim();
    if (v.length < 2) { setError("Type at least 2 characters of the email to search."); return; }
    setParams((prev) => { const next = new URLSearchParams(prev); next.set("email", v); return next; });
  }

  function clearSearch() {
    setInput("");
    setParams((prev) => { const next = new URLSearchParams(prev); next.delete("email"); return next; });
  }

  return (
    <>
      <div className="page-head reveal" data-d="1">
        <div className="crumb">Support · Organizations · Invitations</div>
        <h1>Organization invitations</h1>
        <p>
          Search organization invitations by recipient email. Open the organization, reissue the
          invitation email (re-sends the same email without changing the expiry), or delete the
          invitation — the invite link stops working immediately and the recipient is not notified.
        </p>
      </div>

      <section className="panel reveal" data-d="2">
        <header>
          <span className="idx">01</span> Find invitation
          <span className="hint">
            {latestMode ? "showing the latest 10 — search to filter" : "substring match on recipient email"}
          </span>
        </header>
        <div className="body">
          <form className="inline-form" onSubmit={submit}>
            <input
              type="search"
              placeholder="alice@example.com"
              spellCheck={false}
              autoComplete="off"
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
            <button className={"btn btn-primary" + (loading ? " loading" : "")} disabled={loading || input.trim().length < 2}>
              <span className="glyph" /> Search
            </button>
            {!latestMode && (
              <button type="button" className="btn btn-ghost" onClick={clearSearch} title="Back to the latest 10">
                Clear
              </button>
            )}
          </form>

          {error && (
            <div className="callout error" style={{ marginTop: 16 }}>
              <span className="mk">err</span>
              <span>{error}</span>
            </div>
          )}

          {results && (
            <div style={{ marginTop: 18, border: "1px solid var(--line)" }}>
              <table className="grid">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Organization</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {results.length === 0 && (
                    <tr>
                      <td className="empty" colSpan={6}>
                        {latestMode ? "no pending invitations" : <>no invitations matched <span className="mono">{email}</span></>}
                      </td>
                    </tr>
                  )}
                  {results.map((inv) => (
                    <tr key={inv.id}>
                      <td>
                        <CopyableEmail
                          email={inv.recipientEmail}
                          linkTo={inv.recipientEmail
                            ? `/support/users?q=${encodeURIComponent(inv.recipientEmail)}&open=1`
                            : undefined}
                        />
                      </td>
                      <td>{inv.organizationName ?? <span className="faint">(unnamed)</span>}</td>
                      <td><StatusTag status={inv.status} /></td>
                      <td className="mono">{fmtDate(inv.createdDateTimeUtc)}</td>
                      <td className="mono">{fmtDate(inv.expiryDateUtc)}</td>
                      <td className="actions">
                        <span style={{ display: "inline-flex", gap: 8 }}>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => navigate(`/support/organizations?org=${encodeURIComponent(inv.organizationId)}`)}
                            title="Open this organization"
                          >
                            Open
                          </button>
                          <ReissueButton onReissue={() => api.resendOrganizationInvite(inv.id)} />
                          <ReissueButton
                            label="Delete"
                            busyLabel="Deleting…"
                            doneLabel="deleted ✓"
                            danger
                            onReissue={() => api.deleteOrganizationInvite(inv.id)}
                            onDone={() => setResults((rows) => rows?.filter((r) => r.id !== inv.id) ?? rows)}
                          />
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}
